

export function Kpi({ label, value, tone, icon: Icon, hint, testId }) {
  return (
    <div className="border border-[#E4E4E0] rounded-sm p-3 bg-[#FFFFFF]" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`font-head text-xl font-black tracking-tight mt-1 flex items-center gap-1 ${tone || "text-gray-900"}`}>
        {Icon && <Icon size={16} weight="bold" />}{value}
      </div>
      {hint && <div className="font-mono text-[9px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

/** Small dialog to pin / clear a manually adjusted offer on one deal row. */

export function Th({ label, align, sortKey, sort, onSort }) {
  const active = sortKey && sort?.key === sortKey;
  const cls = `px-3 py-2 text-[10px] uppercase tracking-wider font-bold ${
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"
  } ${sortKey ? "cursor-pointer select-none hover:text-black" : ""} ${active ? "text-[#E3000B]" : "text-gray-500"}`;
  if (!sortKey) return <th className={cls}>{label}</th>;
  return (
    <th className={cls} onClick={() => onSort(sortKey)} data-testid={`deals-sort-${sortKey}`}
        aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
        title={`Sort by ${label.toLowerCase()}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[8px] ${active ? "opacity-100" : "opacity-30"}`}>{active && sort.dir === "asc" ? "▲" : "▼"}</span>
      </span>
    </th>
  );
}

// Value extractors for each sortable column. null = "no data" → always last.
const SORT_GETTERS = {
  set: r => r.set_number || null,
  name: r => (r.name || r.title || "").toLowerCase() || null,
  asin: r => ((r.last_scan_amazon || {}).asin || r.asin) ? 1 : 0,
  qty: r => r.qty ?? null,
  paid: r => r.buy_cost ?? null,
  amz_bb: r => (r.last_scan_amazon || {}).buy_box ?? r.current_buy_box ?? null,
  stock: r => { const a = r.last_scan_amazon || {}; return a.scanned_at ? (a.in_stock ? 1 : 0) : null; },
  rank: r => (r.last_scan_amazon || {}).sales_rank ?? null,
  bl_lowest: r => (r.last_scan_bricklink || {}).lowest_sealed_us ?? null,
  bl_avg: r => (r.last_scan_bricklink || {}).avg_sealed ?? null,
  pc_sealed: r => (r.last_scan_pricecharting || {}).sealed_price ?? null,
  suggested: r => {
    const o = r.suggested_offer; if (!o?.available) return null;
    return o.mode === "max_buy" ? o.max_buy_at_target_roi : o.recommended;
  },
  smart_offer: r => { const o = r.smart_offer; return o?.available ? o.recommended : null; },
  approve: r => (r.offer_approved ? 1 : 0),
  roi: r => { const o = r.suggested_offer; if (!o?.available || o.mode !== "offer") return null; return o.roi_at_buy_box ?? o.roi_at_recommended ?? null; },
  unit_pl: r => { const bb = (r.last_scan_amazon || {}).buy_box ?? r.current_buy_box; return (r.buy_cost != null && bb != null) ? bb - r.buy_cost : null; },
  last_scan: r => [r.last_scan_amazon?.scanned_at, r.last_scan_bricklink?.scanned_at, r.last_scan_pricecharting?.scanned_at].filter(Boolean).sort().reverse()[0] || null,
};

export function sortRows(rows, sort) {
  if (!sort?.key || !SORT_GETTERS[sort.key]) return rows;
  const get = SORT_GETTERS[sort.key];
  const dir = sort.dir === "asc" ? 1 : -1;
  const setNum = (s) => { const m = /^(\d+)(?:-(\d+))?/.exec(s || ""); return m ? [parseInt(m[1], 10), parseInt(m[2] || "0", 10)] : null; };
  return [...rows].sort((a, b) => {
    const va = get(a), vb = get(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (sort.key === "set") {
      const na = setNum(va), nb = setNum(vb);
      if (na && nb) return dir * ((na[0] - nb[0]) || (na[1] - nb[1]));
    }
    if (typeof va === "string" || typeof vb === "string") return dir * String(va).localeCompare(String(vb));
    return dir * (va - vb);
  });
}
